import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { DollarSign, Plus, Trash2, Edit3, X, Check } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const currency = (n) => `$${Number(n || 0).toFixed(2)}`;
const empty = { inspector_user_id: "", inspector_name: "", inspector_email: "", regular_rate: 0, internal_rate: 0, external_rate: 0 };

/**
 * AdminInspectorRatesPage — Angela Feb 2026
 * Admin CRUD for the inspector × flow_type payment matrix (PRD Item 4).
 * The Pre-route screen uses these rates to compute the daily payout.
 */
const AdminInspectorRatesPage = ({ Layout }) => {
  const [items, setItems] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/admin/inspector-rates`);
      setItems(r.data.items || []);
      setUsers(r.data.users || []);
    } catch (e) {
      setError(e.response?.data?.detail || "Failed to load rates");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openNew = () => { setEditing({}); setForm(empty); setError(""); };
  const openEdit = (a) => {
    setEditing(a);
    setForm({
      inspector_user_id: a.inspector_user_id,
      inspector_name: a.inspector_name,
      inspector_email: a.inspector_email,
      regular_rate: a.regular_rate,
      internal_rate: a.internal_rate,
      external_rate: a.external_rate,
    });
    setError("");
  };
  const close = () => { setEditing(null); setForm(empty); setError(""); };

  const onUserPick = (userId) => {
    const u = users.find(x => x.id === userId);
    setForm({ ...form, inspector_user_id: userId, inspector_name: u?.name || "", inspector_email: u?.email || "" });
  };

  const save = async () => {
    setError("");
    if (!form.inspector_name.trim()) { setError("Inspector name is required."); return; }
    setSaving(true);
    try {
      if (editing && editing.id) {
        await axios.patch(`${API}/admin/inspector-rates/${editing.id}`, form);
      } else {
        await axios.post(`${API}/admin/inspector-rates`, form);
      }
      close(); load();
    } catch (e) {
      setError(e.response?.data?.detail || "Failed to save");
    } finally { setSaving(false); }
  };

  const remove = async (a) => {
    if (!window.confirm(`Delete rate for ${a.inspector_name}?`)) return;
    try { await axios.delete(`${API}/admin/inspector-rates/${a.id}`); load(); }
    catch (e) { alert(e.response?.data?.detail || "Failed to delete"); }
  };

  return (
    <Layout>
      <div className="max-w-5xl mx-auto" data-testid="admin-inspector-rates-page">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-[#D1A054]/15">
              <DollarSign className="h-5 w-5 text-[#D1A054]" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-[#1F2937]">Inspector Payment Rates</h1>
              <p className="text-xs text-gray-500">Payment per order for each inspector, split by Regular / Internal / External.</p>
            </div>
          </div>
          <button
            onClick={openNew}
            className="flex items-center gap-2 px-4 py-2 bg-[#2F5D8A] hover:bg-[#3B6D9C] text-white text-sm font-semibold rounded-lg transition-colors"
            data-testid="rate-new-btn"
          >
            <Plus className="h-4 w-4" /> New rate
          </button>
        </div>

        {loading ? (
          <div className="text-center py-8 text-gray-400 text-sm">Loading…</div>
        ) : items.length === 0 ? (
          <div className="text-center py-12 bg-white border border-gray-200 rounded-lg" data-testid="rates-empty">
            <DollarSign className="h-10 w-10 text-gray-300 mx-auto mb-2" />
            <p className="text-sm text-gray-500">No rates configured yet.</p>
          </div>
        ) : (
          <div className="bg-white border border-gray-200 rounded-lg overflow-hidden" data-testid="rates-list">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-200">
                  <th className="text-left px-4 py-2 text-[11px] font-bold text-gray-500 uppercase tracking-wider">Inspector</th>
                  <th className="text-right px-4 py-2 text-[11px] font-bold text-gray-500 uppercase tracking-wider">Regular</th>
                  <th className="text-right px-4 py-2 text-[11px] font-bold text-gray-500 uppercase tracking-wider">Internal</th>
                  <th className="text-right px-4 py-2 text-[11px] font-bold text-gray-500 uppercase tracking-wider">External</th>
                  <th className="px-4 py-2 w-24"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {items.map((a) => (
                  <tr key={a.id} data-testid={`rate-row-${a.id}`}>
                    <td className="px-4 py-2.5">
                      <p className="font-semibold text-[#1F2937]">{a.inspector_name}</p>
                      {a.inspector_email && <p className="text-[10px] text-gray-400">{a.inspector_email}</p>}
                    </td>
                    <td className="text-right px-4 py-2.5 font-mono">{currency(a.regular_rate)}</td>
                    <td className="text-right px-4 py-2.5 font-mono">{currency(a.internal_rate)}</td>
                    <td className="text-right px-4 py-2.5 font-mono">{currency(a.external_rate)}</td>
                    <td className="px-4 py-2.5">
                      <div className="flex items-center justify-end gap-1">
                        <button onClick={() => openEdit(a)} className="p-1.5 text-gray-500 hover:text-[#2F5D8A]" data-testid={`rate-edit-${a.id}`}>
                          <Edit3 className="h-4 w-4" />
                        </button>
                        <button onClick={() => remove(a)} className="p-1.5 text-gray-500 hover:text-red-600" data-testid={`rate-delete-${a.id}`}>
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {editing !== null && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" data-testid="rate-editor">
            <div className="bg-white rounded-lg max-w-md w-full mx-4 shadow-2xl">
              <div className="flex items-center justify-between px-5 py-3 border-b border-gray-200">
                <h3 className="text-sm font-bold text-[#1F2937]">{editing.id ? "Edit rate" : "New rate"}</h3>
                <button onClick={close} className="p-1 text-gray-500 hover:text-gray-700" data-testid="rate-editor-close"><X className="h-4 w-4" /></button>
              </div>
              <div className="p-5 space-y-4">
                {!editing.id && (
                  <div>
                    <label className="block text-[11px] font-semibold text-gray-500 uppercase tracking-wide mb-1">Link to system user (optional)</label>
                    <select
                      value={form.inspector_user_id}
                      onChange={(e) => onUserPick(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-[#2F5D8A]/30 focus:border-[#2F5D8A]"
                      data-testid="rate-user-select"
                    >
                      <option value="">— Type name manually —</option>
                      {users.map(u => <option key={u.id} value={u.id}>{u.name} ({u.role})</option>)}
                    </select>
                  </div>
                )}
                <div>
                  <label className="block text-[11px] font-semibold text-gray-500 uppercase tracking-wide mb-1">Inspector name</label>
                  <input
                    type="text"
                    value={form.inspector_name}
                    onChange={(e) => setForm({ ...form, inspector_name: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-[#2F5D8A]/30 focus:border-[#2F5D8A]"
                    data-testid="rate-name-input"
                  />
                </div>
                <div className="grid grid-cols-3 gap-3">
                  {[
                    { k: "regular_rate", label: "Regular" },
                    { k: "internal_rate", label: "Internal" },
                    { k: "external_rate", label: "External" },
                  ].map(f => (
                    <div key={f.k}>
                      <label className="block text-[11px] font-semibold text-gray-500 uppercase tracking-wide mb-1">{f.label}</label>
                      <div className="relative">
                        <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400 text-sm">$</span>
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          value={form[f.k]}
                          onChange={(e) => setForm({ ...form, [f.k]: parseFloat(e.target.value) || 0 })}
                          className="w-full pl-6 pr-3 py-2 border border-gray-300 rounded-lg text-sm font-mono focus:ring-2 focus:ring-[#2F5D8A]/30 focus:border-[#2F5D8A]"
                          data-testid={`rate-${f.k}-input`}
                        />
                      </div>
                    </div>
                  ))}
                </div>
                {error && <p className="text-xs text-red-600" data-testid="rate-editor-error">{error}</p>}
              </div>
              <div className="flex items-center justify-end gap-2 px-5 py-3 bg-gray-50 border-t border-gray-200 rounded-b-lg">
                <button onClick={close} className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800">Cancel</button>
                <button
                  onClick={save}
                  disabled={saving}
                  className="flex items-center gap-2 px-4 py-2 bg-[#D1A054] hover:bg-[#B98F42] text-white text-sm font-semibold rounded-lg disabled:opacity-50"
                  data-testid="rate-save-btn"
                >
                  <Check className="h-4 w-4" /> {saving ? "Saving…" : "Save"}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default AdminInspectorRatesPage;
